import React, { useState, useEffect } from 'react';
import { AppUser } from '../../types';
import { CIEN_CUADRAS_BLUE, CIEN_CUADRAS_BUTTON_BLUE } from '../../firebase/firebaseConfig';
import { CienCuadrasLogoImg, SpinnerIcon, LogoutIcon } from '../../components/Icons';

interface ZohoOnboardingViewProps {
  appUser: AppUser;
  onCompleteOnboarding: (userId: string, data: any) => Promise<void>;
  onLogout?: () => void;
}

const ADVISORS = [
  { name: "Fabian Sanchez", id: "5761950000183925090" },
  { name: "Lorena Yamile Baquero Cabrejo", id: "5761950000003958437" },
  { name: "Yeni Garay Garcia", id: "5761950000190154715" },
  { name: "Yeny Jaimes", id: "5761950000183925070" },
  { name: "Yudy Cristina Rendon Rico", id: "5761950000003958385" },
  { name: "Brigitte Natalia Monroy Rodríguez", id: "5761950000113768001" },
  { name: "Diana Ortega", id: "5761950000094393169" },
  { name: "Diana Patricia Florez Gutierrez", id: "5761950000092755020" },
  { name: "Leidy Carolina Maldonado Pamplona", id: "5761950000003958463" }
];

const RANDOM_ADVISORS = [
  { name: "Yeny Jaimes", id: "5761950000183925070" },
  { name: "Lorena Yamile Baquero Cabrejo", id: "5761950000003958437" },
  { name: "Fabian Sanchez", id: "5761950000183925090" },
  { name: "Leidy Carolina Maldonado Pamplona", id: "5761950000003958463" }
];

const LINK_TYPES = [
  "Broker independiente",
  "Inmobiliaria",
  "Agente comercial inmobiliaria",
  "Broker del exterior"
];

export const ZohoOnboardingView: React.FC<ZohoOnboardingViewProps> = ({ appUser, onCompleteOnboarding, onLogout }) => {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [loading, setLoading] = useState(true);
  const [zohoId, setZohoId] = useState<string | null>(appUser.id_zoho || null);
  const [originalOwner, setOriginalOwner] = useState<{ id: string; name: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [nombre, setNombre] = useState(appUser.displayName || appUser.nombreCompleto || '');
  const [telefono, setTelefono] = useState(appUser.celular || '');
  const [tipoDocumento, setTipoDocumento] = useState(appUser.tipoIdentificacion || '');
  const [documento, setDocumento] = useState(appUser.numeroIdentificacion || '');
  const [tipoVinculacion, setTipoVinculacion] = useState<string>('');

  // Step 2 advisor states
  const [knowsAdvisor, setKnowsAdvisor] = useState<boolean | null>(null);
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string>('');
  const [wantsToChangeAdvisor, setWantsToChangeAdvisor] = useState<boolean>(false);

  // Inmobiliaria search states
  const [inmobiliariaSearch, setInmobiliariaSearch] = useState('');
  const [inmobiliariaResults, setInmobiliariaResults] = useState<any[]>([]);
  const [selectedInmobiliaria, setSelectedInmobiliaria] = useState<{ id: string; name: string; ownerId?: string; ownerName?: string } | null>(null);
  const [hasSearchedInmobiliaria, setHasSearchedInmobiliaria] = useState(false);
  const [allInmobiliarias, setAllInmobiliarias] = useState<any[]>([]);
  const [isLoadingInmobiliarias, setIsLoadingInmobiliarias] = useState(false);


  useEffect(() => {
    const fetchZohoBrokerData = async () => {
      setLoading(true);
      setError(null);
      let broker: any = null;
      let detectedId: string | null = null;

      try {
        // 1. Try search by ID if present
        if (appUser.id_zoho) {
          try {
            const res = await fetch(`/api/zoho/broker?id=${encodeURIComponent(appUser.id_zoho)}`);
            if (res.ok) {
              const json = await res.json();
              const data = json.data;
              const foundBroker = Array.isArray(data) ? data[0] : data;
              if (foundBroker) {
                broker = foundBroker;
                detectedId = foundBroker.id || appUser.id_zoho;
              }
            }
          } catch (err) {
            console.warn("Could not find broker by ID, falling back to email search", err);
          }
        }

        // 2. If not found by ID, try search by email
        if (!broker && appUser.email) {
          try {
            const res = await fetch(`/api/zoho/broker?email=${encodeURIComponent(appUser.email)}`);
            if (res.ok) {
              const json = await res.json();
              const data = json.data;
              const foundBroker = Array.isArray(data) ? data[0] : data;
              if (foundBroker) {
                broker = foundBroker;
                detectedId = foundBroker.id || null;
              }
            }
          } catch (err) {
            console.error("Could not find broker by email", err);
          }
        }

        // 3. Set states depending on whether we found a broker or not
        if (broker) {
          setZohoId(detectedId);
          if (broker.Name) setNombre(broker.Name);
          if (broker.N_mero_de_celular) setTelefono(broker.N_mero_de_celular);
          else if (broker.N_mero_de_celular_del_Representante_Legal) setTelefono(broker.N_mero_de_celular_del_Representante_Legal);
          if (broker.Tipo_de_documento) setTipoDocumento(broker.Tipo_de_documento);
          if (broker.N_mero_de_documento_de_Identidad) setDocumento(broker.N_mero_de_documento_de_Identidad);
          if (broker.Selecciona_el_tipo_vinculaci_n) setTipoVinculacion(broker.Selecciona_el_tipo_vinculaci_n);
          
          if (broker.Inmobiliaria) {
            setSelectedInmobiliaria({
              id: broker.Inmobiliaria.id || '',
              name: broker.Inmobiliaria.name || ''
            });
            setInmobiliariaSearch(broker.Inmobiliaria.name || '');
          }
          
          if (broker.Owner) {
            const ownerInfo = {
              id: broker.Owner.id || '',
              name: broker.Owner.name || ''
            };
            setOriginalOwner(ownerInfo);
            setSelectedAdvisorId(ownerInfo.id);
          }
        } else {
          setZohoId(null);
        }
      } catch (err: any) {
        console.error("Error in Zoho onboarding search flow:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchZohoBrokerData();
  }, [appUser.id_zoho, appUser.email]);

  useEffect(() => {
    if (tipoVinculacion === 'Agente comercial inmobiliaria' && allInmobiliarias.length === 0 && !isLoadingInmobiliarias) {
      const fetchAllInmobiliarias = async () => {
        setIsLoadingInmobiliarias(true);
        try {
          const res = await fetch('/api/zoho/inmobiliarias');
          if (res.ok) {
            const json = await res.json();
            setAllInmobiliarias(json.data || []);
          }
        } catch (err) {
          console.error("Error loading all inmobiliarias:", err);
        } finally {
          setIsLoadingInmobiliarias(false);
        }
      };
      fetchAllInmobiliarias();
    }
  }, [tipoVinculacion, allInmobiliarias.length, isLoadingInmobiliarias]);

  useEffect(() => {
    if (tipoVinculacion !== 'Agente comercial inmobiliaria') {
      return;
    }

    if (inmobiliariaSearch.length < 2) {
      setInmobiliariaResults([]);
      setHasSearchedInmobiliaria(false);
      return;
    }

    if (selectedInmobiliaria && selectedInmobiliaria.name === inmobiliariaSearch) {
      setInmobiliariaResults([]);
      return;
    }

    const query = inmobiliariaSearch.toLowerCase();
    const filtered = allInmobiliarias.filter((item) =>
      item.Name && item.Name.toLowerCase().includes(query)
    );
    setInmobiliariaResults(filtered);
    setHasSearchedInmobiliaria(true);
  }, [inmobiliariaSearch, tipoVinculacion, selectedInmobiliaria, allInmobiliarias]);

  const handleNextStep = () => {
    if (!nombre.trim()) {
      setError("El nombre es obligatorio.");
      return;
    }
    if (!telefono.trim()) {
      setError("El teléfono celular es obligatorio.");
      return;
    }
    setError(null);
    setStep(2);
  };

  const handleBackStep = () => {
    if (step === 4) {
      setStep(3);
    } else if (step === 3) {
      setKnowsAdvisor(null);
      setWantsToChangeAdvisor(false);
      setStep(2);
    } else {
      setStep(1);
    }
  };

  const handleStep2Submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (knowsAdvisor === null) {
      setError("Por favor responde si conoces a tu comercial asignado.");
      return;
    }
    if (knowsAdvisor && !selectedAdvisorId) {
      setError("Por favor selecciona tu comercial asignado de la lista.");
      return;
    }
    setError(null);
    setStep(3);
  };

  const handleStep3Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tipoVinculacion) {
      setError("Por favor selecciona un tipo de vinculación.");
      return;
    }

    if (tipoVinculacion === 'Agente comercial inmobiliaria' && !selectedInmobiliaria) {
      setError("Por favor busca y selecciona la inmobiliaria a la que estás vinculado.");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let finalZohoId = zohoId;
      const brokerPayload: any = {
        Name: nombre,
        N_mero_de_celular: telefono,
        Tipo_de_documento: tipoDocumento || null,
        N_mero_de_documento_de_Identidad: documento || null,
        Selecciona_el_tipo_vinculaci_n: tipoVinculacion,
      };

      if (tipoVinculacion === 'Agente comercial inmobiliaria' && selectedInmobiliaria) {
        brokerPayload.Inmobiliaria = {
          name: selectedInmobiliaria.name,
          id: selectedInmobiliaria.id
        };
      } else {
        brokerPayload.Inmobiliaria = null;
      }


      // Determine advisor ID
      let advisorId = selectedAdvisorId;
      if (!knowsAdvisor) {
        if (tipoVinculacion === 'Agente comercial inmobiliaria' && selectedInmobiliaria) {
          const matchedInmo = allInmobiliarias.find(inmo => inmo.id === selectedInmobiliaria.id);
          const inmoOwnerId = matchedInmo?.Owner?.id || selectedInmobiliaria.ownerId;
          if (inmoOwnerId) {
            advisorId = inmoOwnerId;
          }
        }
        
        if (!advisorId) {
          if (originalOwner && originalOwner.id) {
            advisorId = originalOwner.id;
          } else {
            // Choose one randomly
            const randomAdv = RANDOM_ADVISORS[Math.floor(Math.random() * RANDOM_ADVISORS.length)];
            advisorId = randomAdv.id;
          }
        }
      }

      brokerPayload.Owner = { id: advisorId };

      if (zohoId) {
        // Update existing broker record
        const res = await fetch(`/api/zoho/broker/${zohoId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: brokerPayload })
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `Error ${res.status} al actualizar en Zoho`);
        }
      } else {
        // Create new broker record
        brokerPayload.Email = appUser.email;
        const res = await fetch('/api/zoho/broker', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: brokerPayload })
        });
        
        if (!res.ok) {
          const errText = await res.text();
          throw new Error(errText);
        }
        
        const responseJson = await res.json();
        // Extract new ID from Zoho response
        if (responseJson.data && responseJson.data.data && responseJson.data.data[0]) {
          const details = responseJson.data.data[0].details;
          if (details && details.id) {
            finalZohoId = details.id;
          }
        }
      }

      // Update local states for display in step 4
      const matchedInmoForSummary = allInmobiliarias.find(inmo => inmo.id === selectedInmobiliaria?.id);
      const inmoOwnerNameForSummary = matchedInmoForSummary?.Owner?.name || selectedInmobiliaria?.ownerName;
      const chosenAdvisor = ADVISORS.find(a => a.id === advisorId) || 
                            RANDOM_ADVISORS.find(a => a.id === advisorId) || 
                            (inmoOwnerNameForSummary ? { name: inmoOwnerNameForSummary, id: advisorId } : null) ||
                            { name: "Asesor Asignado", id: advisorId };
      setOriginalOwner(chosenAdvisor);
      setZohoId(finalZohoId);

      // Transition to Step 4 (Summary Screen)
      setStep(4);
    } catch (err: any) {
      console.error("Error during Zoho processing:", err);
      const errMsg = err.message || "";
      if (errMsg.includes("DUPLICATE_DATA") && errMsg.includes("N_mero_de_celular")) {
        setError("El número de teléfono celular ingresado ya se encuentra registrado con otro broker. Por favor, verifica el número e inténtalo de nuevo.");
        setStep(1); // Return to basic info step
      } else {
        setError(errMsg || "Ocurrió un error al procesar tu registro. Por favor inténtalo de nuevo.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinalSubmit = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await onCompleteOnboarding(appUser.uid, {
        displayName: nombre,
        nombreCompleto: nombre,
        celular: telefono,
        tipoIdentificacion: tipoDocumento || null,
        numeroIdentificacion: documento || null,
        id_zoho: zohoId || null,
        tipoVinculacion: tipoVinculacion || null,
        zohoOnboardingCompleted: true,
      });
    } catch (err: any) {
      setError(err.message || "Error al completar el registro. Inténtalo de nuevo.");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Top Banner with logged-in email and logout option */}
        {onLogout && (
          <div className="w-full bg-slate-800 text-white py-2.5 px-4 sm:px-6 flex justify-between items-center text-xs font-medium shadow-sm">
            <div className="flex items-center space-x-2">
              <span className="bg-slate-700 text-slate-300 px-2 py-0.5 rounded font-mono">Sesión:</span>
              <span className="text-slate-100 font-semibold">{appUser.email || 'Sin correo'}</span>
            </div>
            <button 
              onClick={onLogout}
              className="flex items-center space-x-1.5 text-slate-300 hover:text-white hover:bg-slate-700/50 px-2.5 py-1 rounded-md transition-all active:scale-95"
              aria-label="Cerrar sesión"
            >
              <LogoutIcon className="w-4 h-4 text-red-400" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        )}
        <div className="flex-grow flex flex-col items-center justify-center p-4">
          <SpinnerIcon className="w-12 h-12 text-sky-600 animate-spin mb-4" />
          <p className="text-gray-600 font-medium">Buscando perfil en Zoho CRM...</p>
        </div>
      </div>
    );
  }

  const isNameError = !!error && error.toLowerCase().includes("nombre");
  const isPhoneError = !!error && (error.toLowerCase().includes("teléfono") || error.toLowerCase().includes("celular"));

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      {/* Top Banner with logged-in email and logout option */}
      {onLogout && (
        <div className="w-full bg-slate-800 text-white py-2.5 px-4 sm:px-6 flex justify-between items-center text-xs font-medium shadow-sm">
          <div className="flex items-center space-x-2">
            <span className="bg-slate-700 text-slate-300 px-2 py-0.5 rounded font-mono">Sesión:</span>
            <span className="text-slate-100 font-semibold">{appUser.email || 'Sin correo'}</span>
          </div>
          <button 
            onClick={onLogout}
            className="flex items-center space-x-1.5 text-slate-300 hover:text-white hover:bg-slate-700/50 px-2.5 py-1 rounded-md transition-all active:scale-95"
            aria-label="Cerrar sesión"
          >
            <LogoutIcon className="w-4 h-4 text-red-400" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      )}

      <div className="flex-grow flex flex-col items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
        <div className="text-center mb-4">
          <CienCuadrasLogoImg className="h-10 mx-auto" />
          <h1 className="text-2xl font-bold mt-4" style={{ color: CIEN_CUADRAS_BLUE }}>Información de Broker</h1>
        </div>

        {/* Visual Stepper */}
        <div className="flex items-center justify-between max-w-md mx-auto mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          {[
            { num: 1, label: "Contacto" },
            { num: 2, label: "Comercial" },
            { num: 3, label: "Vinculación" },
            { num: 4, label: "Resumen" }
          ].map((s, idx) => {
            const isActive = step === s.num;
            const isCompleted = step > s.num;
            return (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center flex-1 relative">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                    isActive 
                      ? 'bg-sky-600 text-white ring-4 ring-sky-100 scale-105 shadow' 
                      : isCompleted 
                        ? 'bg-green-500 text-white shadow-sm' 
                        : 'bg-gray-100 text-gray-400 border border-gray-200'
                  }`}>
                    {isCompleted ? '✓' : s.num}
                  </div>
                  <span className={`text-[10px] mt-1.5 font-medium transition-colors duration-300 whitespace-nowrap ${
                    isActive ? 'text-sky-600 font-bold' : isCompleted ? 'text-green-600 font-medium' : 'text-gray-400'
                  }`}>
                    {s.label}
                  </span>
                </div>
                {idx < 3 && (
                  <div className="flex-1 h-0.5 max-w-[30px] -mt-5">
                    <div className={`h-full w-full rounded transition-all duration-500 ${
                      step > s.num ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
          {/* Zoho ID notification banner (Only show on Step 1, 2 & 3 if we actually have one) */}
          {step !== 4 && (
            zohoId ? (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex flex-col gap-1 animate-fade-in">
                <span className="font-semibold">✓ Vinculado con Zoho CRM</span>
                <span className="text-xs text-green-600 font-mono">Zoho ID detectado: {zohoId}</span>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm flex flex-col gap-1 animate-fade-in">
                <span className="font-semibold">ℹ No vinculado automáticamente</span>
                <span className="text-xs text-amber-600">Por favor, introduce tus datos manualmente.</span>
              </div>
            )
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm leading-relaxed">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre Completo *</label>
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 text-sm transition-all duration-200 ${
                    isNameError 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50/30' 
                      : 'border-gray-300 focus:ring-sky-500 focus:border-sky-500'
                  }`}
                  placeholder="Ej. Juan Pérez"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Teléfono Celular *</label>
                <input
                  type="tel"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 text-sm transition-all duration-200 ${
                    isPhoneError 
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50/30' 
                      : 'border-gray-300 focus:ring-sky-500 focus:border-sky-500'
                  }`}
                  placeholder="Ej. 3001234567"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo de Documento</label>
                  <select
                    value={tipoDocumento}
                    onChange={(e) => setTipoDocumento(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500 text-sm"
                  >
                    <option value="">Ninguno</option>
                    <option value="Cédula de Ciudadanía">Cédula de Ciudadanía</option>
                    <option value="Cédula de Extranjería">Cédula de Extranjería</option>
                    <option value="NIT">NIT</option>
                    <option value="Pasaporte">Pasaporte</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Documento</label>
                  <input
                    type="text"
                    value={documento}
                    onChange={(e) => setDocumento(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500"
                    placeholder="Número"
                  />
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="button"
                  onClick={handleNextStep}
                  style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                  className="w-full py-2.5 px-4 text-white font-medium rounded-md shadow hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 transition-opacity"
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {knowsAdvisor === null && (
                <div className="space-y-6">
                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 text-center shadow-sm">
                    <p className="text-gray-800 font-semibold mb-5 text-sm sm:text-base leading-relaxed">
                      ¿Sabes quién es tu Comercial Asignado?
                    </p>
                    <div className="flex gap-4 justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          setKnowsAdvisor(true);
                          setWantsToChangeAdvisor(false);
                          if (originalOwner) {
                            setSelectedAdvisorId(originalOwner.id);
                          } else {
                            setSelectedAdvisorId('');
                          }
                        }}
                        className="px-8 py-3 rounded-lg font-bold border text-sm transition-all shadow-sm bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                      >
                        Sí
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setKnowsAdvisor(false);
                          setSelectedAdvisorId('');
                          setError(null);
                          // Jump immediately to the next step
                          setStep(3);
                        }}
                        className="px-8 py-3 rounded-lg font-bold border text-sm transition-all shadow-sm bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                      >
                        No
                      </button>
                    </div>
                  </div>

                  <div className="flex pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={handleBackStep}
                      className="w-full py-2.5 px-4 text-gray-700 font-semibold rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none transition-all duration-200 text-sm"
                    >
                      Atrás
                    </button>
                  </div>
                </div>
              )}

              {knowsAdvisor === true && originalOwner && originalOwner.id && !wantsToChangeAdvisor && (
                <div className="space-y-6 animate-fade-in">
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-6 text-center space-y-4 shadow-sm">
                    <p className="text-gray-700 text-sm font-medium">
                      Según nuestros registros en Zoho, tu comercial asignado es:
                    </p>
                    <p className="text-xl font-extrabold text-sky-800 tracking-tight">
                      {originalOwner.name}
                    </p>
                    <p className="text-gray-800 font-semibold text-sm pt-2">
                      ¿Es correcto?
                    </p>
                  </div>
                  
                  <div className="flex gap-4 justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedAdvisorId(originalOwner.id);
                        setError(null);
                        setStep(3);
                      }}
                      style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                      className="px-6 py-2.5 text-white font-semibold rounded-md shadow-md hover:opacity-90 transition-opacity text-sm"
                    >
                      Sí, es correcto
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setWantsToChangeAdvisor(true);
                      }}
                      className="px-6 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-md font-semibold hover:bg-gray-50 transition-all text-sm"
                    >
                      No, cambiarlo
                    </button>
                  </div>

                  <div className="flex pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setKnowsAdvisor(null)}
                      className="w-full py-2.5 px-4 text-gray-700 font-semibold rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none transition-all duration-200 text-sm"
                    >
                      Atrás
                    </button>
                  </div>
                </div>
              )}

              {knowsAdvisor === true && (wantsToChangeAdvisor || !originalOwner || !originalOwner.id) && (
                <div className="space-y-5 animate-fade-in">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Selecciona tu Comercial Asignado de la lista:
                    </label>
                    <select
                      value={selectedAdvisorId}
                      onChange={(e) => setSelectedAdvisorId(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500 text-sm"
                      disabled={isSaving}
                    >
                      <option value="">-- Selecciona un Asesor Comercial --</option>
                      {ADVISORS.map(adv => (
                        <option key={adv.id} value={adv.id}>{adv.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-4 pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => {
                        if (originalOwner && originalOwner.id) {
                          setWantsToChangeAdvisor(false);
                        } else {
                          setKnowsAdvisor(null);
                        }
                      }}
                      disabled={isSaving}
                      className="w-1/2 py-2.5 px-4 text-gray-700 font-semibold rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none disabled:opacity-50 transition-opacity text-sm"
                    >
                      Atrás
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedAdvisorId) {
                          setError("Por favor selecciona tu comercial asignado de la lista.");
                          return;
                        }
                        setError(null);
                        setStep(3);
                      }}
                      disabled={isSaving}
                      style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                      className="w-1/2 py-2.5 px-4 text-white font-semibold rounded-md shadow hover:opacity-90 focus:outline-none flex items-center justify-center disabled:opacity-50 transition-opacity text-sm"
                    >
                      Continuar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <form onSubmit={handleStep3Submit} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Selecciona el tipo de vinculación *
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Este campo es obligatorio y define tu modalidad de trabajo con CienCuadras.
                  </p>
                  <select
                    value={tipoVinculacion}
                    onChange={(e) => {
                      setTipoVinculacion(e.target.value);
                      if (e.target.value !== 'Agente comercial inmobiliaria') {
                        setSelectedInmobiliaria(null);
                        setInmobiliariaSearch('');
                        setInmobiliariaResults([]);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500 text-sm"
                    disabled={isSaving}
                  >
                    <option value="">-- Selecciona una opción --</option>
                    {LINK_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {tipoVinculacion === 'Agente comercial inmobiliaria' && (
                  <div className="mt-4 space-y-2 animate-fade-in border-t border-gray-100 pt-4">
                    <label className="block text-sm font-semibold text-gray-700">
                      Buscar Inmobiliaria *
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Escribe al menos dos caracteres para buscar la inmobiliaria registrada en el sistema.
                    </p>
                    <div className="relative">
                      <input
                        type="text"
                        value={inmobiliariaSearch}
                        onChange={(e) => {
                          setInmobiliariaSearch(e.target.value);
                          if (selectedInmobiliaria && selectedInmobiliaria.name !== e.target.value) {
                            setSelectedInmobiliaria(null);
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500 text-sm font-sans"
                        placeholder={isLoadingInmobiliarias ? "Cargando catálogo de inmobiliarias..." : "Buscar por nombre de inmobiliaria..."}
                        disabled={isSaving || isLoadingInmobiliarias}
                      />
                      {isLoadingInmobiliarias && (
                        <div className="absolute right-3 top-2.5">
                          <SpinnerIcon className="w-4 h-4 text-sky-600 animate-spin" />
                        </div>
                      )}
                    </div>

                    {/* Selected Inmobiliaria Badge */}
                    {selectedInmobiliaria && (
                      <div className="flex items-center justify-between p-2.5 bg-sky-50 border border-sky-200 rounded-md text-sky-800 text-xs">
                        <div className="flex flex-col">
                          <span className="font-semibold">Seleccionado: {selectedInmobiliaria.name}</span>
                          <span className="text-[10px] text-sky-600 font-mono">ID: {selectedInmobiliaria.id}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedInmobiliaria(null);
                            setInmobiliariaSearch('');
                          }}
                          className="text-sky-600 hover:text-sky-800 font-bold px-2 text-sm"
                        >
                          ✕
                        </button>
                      </div>
                    )}

                    {/* Search Results Dropdown/List */}
                    {!selectedInmobiliaria && inmobiliariaResults.length > 0 && (
                      <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-48 overflow-y-auto bg-white shadow-sm">
                        {inmobiliariaResults.slice(0, 5).map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => {
                              setSelectedInmobiliaria({
                                id: item.id,
                                name: item.Name,
                                ownerId: item.Owner?.id || '',
                                ownerName: item.Owner?.name || ''
                              });
                              setInmobiliariaSearch(item.Name);
                              setInmobiliariaResults([]);
                            }}
                            className="w-full text-left px-3 py-3.5 text-xs hover:bg-gray-50 flex flex-col transition-colors"
                          >
                            <span className="font-semibold text-gray-800">{item.Name}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Warning Callout when not found */}
                    {hasSearchedInmobiliaria && inmobiliariaResults.length === 0 && !selectedInmobiliaria && inmobiliariaSearch.length >= 2 && (
                      <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs leading-relaxed animate-fade-in space-y-1">
                        <p className="font-semibold">⚠️ La inmobiliaria no existe</p>
                        <p>
                          Si no encuentras tu inmobiliaria en el sistema, por favor selecciona el tipo de vinculación <strong className="font-semibold">"Inmobiliaria"</strong> en el menú superior para registrarla con tu mismo nombre e ID de broker.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-4 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleBackStep}
                  disabled={isSaving}
                  className="w-1/2 py-2.5 px-4 text-gray-700 font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none disabled:opacity-50 transition-opacity"
                >
                  Atrás
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                  className="w-1/2 py-2.5 px-4 text-white font-medium rounded-md shadow hover:opacity-90 focus:outline-none flex items-center justify-center disabled:opacity-50 transition-opacity"
                >
                  {isSaving && <SpinnerIcon className="w-4 h-4 mr-2 animate-spin" />}
                  {isSaving ? 'Guardando...' : 'Siguiente'}
                </button>
              </div>
            </form>
          )}


          {step === 4 && (
            <div className="space-y-6 animate-fade-in">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 text-sm flex items-center gap-3">
                <span className="text-xl">✓</span>
                <div>
                  <p className="font-semibold">¡Información sincronizada correctamente!</p>
                  <p className="text-xs text-green-700">Por favor confirma el resumen de tus datos antes de continuar.</p>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Resumen de Datos</span>
                  {zohoId && (
                    <span className="text-[11px] font-mono bg-sky-100 text-sky-800 px-2 py-0.5 rounded">
                      ID Zoho: {zohoId}
                    </span>
                  )}
                </div>

                <div className="p-4 space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Nombre Completo:</span>
                    <span className="font-semibold text-gray-900">{nombre}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Teléfono Celular:</span>
                    <span className="font-semibold text-gray-900">{telefono}</span>
                  </div>
                  {tipoDocumento && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Tipo de Documento:</span>
                      <span className="font-semibold text-gray-900">{tipoDocumento}</span>
                    </div>
                  )}
                  {documento && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Documento:</span>
                      <span className="font-semibold text-gray-900">{documento}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tipo de Vinculación:</span>
                    <span className="font-semibold text-gray-900 text-right">{tipoVinculacion}</span>
                  </div>
                  {tipoVinculacion === 'Agente comercial inmobiliaria' && selectedInmobiliaria && (
                    <div className="flex justify-between border-t border-gray-100 pt-2 mt-2">
                      <span className="text-gray-500">Inmobiliaria Vinculada:</span>
                      <span className="font-semibold text-gray-900 text-right">{selectedInmobiliaria.name}</span>
                    </div>
                  )}

                  <div className="flex justify-between border-t border-gray-100 pt-3">
                    <span className="text-gray-500">Comercial Asignado:</span>
                    <span className="font-semibold text-gray-900 text-right">
                      {originalOwner ? originalOwner.name : 'Asignación automática'}
                    </span>
                  </div>
                  {originalOwner && originalOwner.id && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">ID Comercial:</span>
                      <span className="font-mono text-xs text-gray-600">{originalOwner.id}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleBackStep}
                  disabled={isSaving}
                  className="w-1/3 py-2.5 px-4 text-gray-700 font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none disabled:opacity-50 transition-opacity"
                >
                  Atrás
                </button>
                <button
                  type="button"
                  onClick={handleFinalSubmit}
                  disabled={isSaving}
                  style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                  className="w-2/3 py-2.5 px-4 text-white font-medium rounded-md shadow hover:opacity-90 focus:outline-none flex items-center justify-center disabled:opacity-50 transition-opacity"
                >
                  {isSaving && <SpinnerIcon className="w-4 h-4 mr-2 animate-spin" />}
                  {isSaving ? 'Completando...' : 'Confirmar y Finalizar'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};
